import { Link } from 'react-router'
import type { CatalogCard } from '@/types/card'
import { baseUrl } from '@/services/catalog'
import { useApp, useCardUpdate } from '@/store/app'
import { OfflineMark, OrgStripe, UpdateMark } from './primitives'

/**
 * Favoritenkachel für die Startseite.
 *
 * Das Vorschaubild ist hier kein Schmuck: Wer eine Karte kennt, erkennt sie am Layout
 * schneller als am Titel. Deshalb steht das Bild oben und der Titel darunter — und
 * nicht umgekehrt.
 */
export function CardTile({ card }: { card: CatalogCard }) {
  const state = useApp((s) => s.offlineStates.get(card.id)) ?? 'fehlt'
  const update = useCardUpdate(card.id)

  return (
    <Link
      to={`/karte/${card.id}`}
      className="group relative flex flex-col overflow-hidden rounded-card border border-line bg-surface
                 transition-colors duration-100 hover:border-line-strong focus-visible:border-accent"
    >
      <OrgStripe organization={card.organization} />

      {/* Einheitlich flacher Ausschnitt vom oberen Kartenrand — auch bei Hochformat.
          Wiedererkannt wird eine Karte an ihrem Kopf (Farbe und Titel); die ganze
          Seite zu zeigen machte die Kacheln hoch und die Startseite lang, ohne dass
          man mehr erkennt. */}
      <div className="relative aspect-[3/2] overflow-hidden bg-sunken">
        <img
          src={new URL(card.thumb, baseUrl).href}
          alt=""
          loading="lazy"
          decoding="async"
          className="size-full object-cover object-top"
        />
        <span className="absolute right-1.5 top-1.5">
          <OfflineMark state={state} />
        </span>
        {/* Gerade bei Favoriten zählt der Hinweis: Es sind die Karten, die jemand im
            Einsatz ohne nachzudenken aufschlägt. */}
        {update && (
          <span className="absolute bottom-1.5 left-2.5 bg-surface">
            <UpdateMark kind={update} />
          </span>
        )}
      </div>

      <div className="flex min-h-14 flex-1 items-center gap-2 px-3 py-2 pl-4">
        {/* Silbentrennung statt Abschneiden: Merriweather läuft breit, und deutsche
            Komposita wie „Atemschutzüberwachung" passen sonst in keine Kachel. */}
        <span
          lang="de"
          className="line-clamp-3 text-sm font-bold leading-snug text-ink-strong
                     [hyphens:auto]"
        >
          {card.title}
        </span>
      </div>
    </Link>
  )
}
